import { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import authService from "../../Services/authService";
import { DEFAULT_MASCOT, normalizeMascot } from "./mascotMood";

// Mirrors NameItApp/components/Mascot/useMyMascot.ts: same users/{uid}.mascot field.
const CACHE_KEY = "nameit.myMascot";
const EVENT = "nameit-my-mascot-change";

function readCache() {
  try {
    return normalizeMascot(JSON.parse(localStorage.getItem(CACHE_KEY)));
  } catch {
    return { ...DEFAULT_MASCOT };
  }
}

function writeCache(m) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(m));
  } catch {
    // Storage blocked: the account copy is still the source of truth.
  }
  window.dispatchEvent(new CustomEvent(EVENT));
}

let loadedFromAccount = false;

export async function loadMascotFromExistingAccount(deps = {}) {
  const api = { authService, db, doc, getDoc, ...deps };
  await api.authService.waitForInit?.();
  const uid = api.authService.getCurrentUserIdIfExists?.();
  if (!uid) return null;
  const snap = await api.getDoc(api.doc(api.db, "users", uid));
  const saved = snap.exists() ? snap.data().mascot : null;
  return saved ? normalizeMascot(saved) : null;
}

async function loadFromAccount() {
  if (loadedFromAccount) return;
  loadedFromAccount = true;
  try {
    const saved = await loadMascotFromExistingAccount();
    if (saved) writeCache(saved);
  } catch (err) {
    loadedFromAccount = false;
    console.error("Failed to load mascot from account:", err);
  }
}

// Saves locally right away (so every screen updates instantly), then to the account.
export async function saveMyMascot(next) {
  const m = normalizeMascot(next);
  writeCache(m);
  try {
    const uid = await authService.getCurrentUserId();
    if (uid) await setDoc(doc(db, "users", uid), { mascot: { ...m, updatedAt: new Date() } }, { merge: true });
  } catch (err) {
    console.error("Failed to save mascot to account:", err);
  }
}

/** The player's own mascot descriptor: { base, color, face }. */
export default function useMyMascot() {
  const [mascot, setMascot] = useState(readCache);
  useEffect(() => {
    const sync = () => setMascot(readCache());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    loadFromAccount();
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return mascot;
}
